<script setup lang="ts">
/**
 * OverlayMockup — 加载遮罩 mockup
 * v5: hide.js yfb-overlay 是 CSS 动画进度条（width 0% → 100%）
 * 全屏遮罩 + 真实 logo + 防止网页加载瞬间剧透
 */
import { ref } from 'vue'
import YfbIcon from '@/components/YfbIcon.vue'

const progress = 65
const count = 12
</script>

<template>
  <div class="ov-mock">
    <!-- 底层内容（被遮罩挡住，模糊可见） -->
    <div class="ov-mock__bg">
      <div class="ov-mock__bg-title">[隐分宝：视频标题]</div>
      <div class="ov-mock__bg-score">▢ : ▢</div>
      <div class="ov-mock__bg-meta">[隐分宝：联赛] · [隐分宝：时间]</div>
    </div>

    <!-- 遮罩层 -->
    <div class="ov-mock__overlay">
      <div class="ov-mock__card">
        <!-- Logo（使用真实 logo 图片） -->
        <div class="ov-mock__logo">
          <img
            src="/icons/logo.png"
            alt="YFB · 隐分宝"
            class="ov-mock__logo-img"
          />
        </div>

        <!-- 状态文字 -->
        <div class="ov-mock__status">防止页面瞬间剧透</div>

        <!-- 进度条 -->
        <div class="ov-mock__progress">
          <div class="ov-mock__progress-bar">
            <div class="ov-mock__progress-fill" :style="{ width: progress + '%' }"></div>
          </div>
          <div class="ov-mock__progress-pct">{{ progress }}%</div>
        </div>

        <!-- 剧透元素计数 -->
        <div class="ov-mock__count">
          <YfbIcon name="lucide:shield-check" :size="12" />
          <span>处理中 · {{ count }} 个剧透元素</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ov-mock {
  position: relative;
  height: 280px;
  border: var(--yfb-border-3) solid var(--yfb-rule);
  background-color: var(--yfb-surface);
  border-radius: var(--yfb-radius-lg);
  box-shadow: var(--yfb-shadow-2);
  overflow: hidden;
}

/* 底层模糊内容（被遮罩） */
.ov-mock__bg {
  position: absolute;
  inset: 0;
  padding: var(--yfb-sp-6);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--yfb-sp-3);
  background-color: var(--yfb-surface-2);
  filter: blur(2px);
  opacity: 0.4;
}

.ov-mock__bg-title {
  font-family: var(--yfb-mono);
  font-size: var(--yfb-fs-md);
  font-weight: 700;
  color: var(--yfb-ink);
  padding: var(--yfb-sp-2) var(--yfb-sp-3);
  background-color: var(--yfb-surface);
  border: 1.5px dashed var(--yfb-rule-soft);
  border-radius: var(--yfb-radius-sm);
  align-self: flex-start;
}

.ov-mock__bg-score {
  font-family: var(--yfb-mono);
  font-size: var(--yfb-fs-3xl);
  font-weight: 700;
  color: var(--yfb-ink);
  text-align: center;
  letter-spacing: var(--yfb-ls-eyebrow);
}

.ov-mock__bg-meta {
  font-family: var(--yfb-mono);
  font-size: 11px;
  font-weight: 700;
  color: var(--yfb-text-secondary);
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  text-align: center;
}

/* 遮罩层 */
.ov-mock__overlay {
  position: absolute;
  inset: 0;
  background-color: var(--yfb-veil);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--yfb-sp-6);
}

.ov-mock__card {
  width: 100%;
  max-width: 320px;
  background-color: var(--yfb-surface);
  border: var(--yfb-border-3) solid var(--yfb-rule);
  border-radius: var(--yfb-radius-lg);
  box-shadow: var(--yfb-shadow-3);
  padding: var(--yfb-sp-6) var(--yfb-sp-5);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--yfb-sp-4);
}

/* Logo */
.ov-mock__logo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: calc(var(--yfb-sp-1) + var(--yfb-sp-1) / 2);
}

.ov-mock__logo-img {
  display: block;
  width: 56px;
  height: 56px;
  object-fit: contain;
  background-color: var(--yfb-accent);
  border-radius: var(--yfb-radius-sm);
  padding: var(--yfb-sp-2);
  border: var(--yfb-border-3) solid var(--yfb-rule);
}

/* 状态 */
.ov-mock__status {
  font-size: var(--yfb-fs-md);
  font-weight: 700;
  letter-spacing: var(--yfb-ls-button);
  color: var(--yfb-ink);
  text-align: center;
  line-height: 1.4;
}

/* 进度条 */
.ov-mock__progress {
  width: 100%;
  display: flex;
  align-items: center;
  gap: var(--yfb-sp-3);
}

.ov-mock__progress-bar {
  flex: 1;
  height: 12px;
  background-color: var(--yfb-surface-2);
  border: 2px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
  position: relative;
  overflow: hidden;
}

.ov-mock__progress-fill {
  height: 100%;
  background-color: var(--yfb-accent);
  border-radius: var(--yfb-radius-sm);
  /* CSS 动画：0% → 100% 模拟 v5 hide.js progressBar */
  animation: ov-progress 2.5s ease-out forwards;
}

@keyframes ov-progress {
  from { width: 0%; }
  to { width: 65%; }
}

.ov-mock__progress-pct {
  font-family: var(--yfb-mono);
  font-size: var(--yfb-fs-sm);
  font-weight: 700;
  color: var(--yfb-ink);
  letter-spacing: var(--yfb-ls-mono);
  flex-shrink: 0;
  min-width: 36px;
  text-align: right;
}

/* 计数 */
.ov-mock__count {
  display: inline-flex;
  align-items: center;
  gap: calc(var(--yfb-sp-1) + var(--yfb-sp-1) / 2);
  padding: var(--yfb-sp-1) var(--yfb-sp-2);
  background-color: var(--yfb-accent);
  color: var(--yfb-on-accent);
  font-family: var(--yfb-mono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: var(--yfb-ls-mono);
  border: 1.5px solid var(--yfb-rule);
  border-radius: var(--yfb-radius-sm);
}

@media (prefers-reduced-motion: reduce) {
  .ov-mock__progress-fill {
    animation: none;
    width: 65%;
  }
}
</style>
